import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { tournamentAPI } from '../../services/api';
import { Card, StatusBadge, Modal, ConfirmModal, Ico, Icons, OrganizerDashboardSkeleton, TournamentCard } from '../../components/common';

const emptyForm = { name: '', type: 'Knockout', matchType: 'Singles', ageGroup: 'Open', status: 'upcoming', surface: 'Hard Court', startDate: '', endDate: '', registrationDeadline: '', location: '', prize: '', winnerPrize: '', runnerUpPrize: '', entryFee: 0, upiId: '', maxPlayers: 8, description: '', rules: '', venueMap: '', parking: '', contactName: '', contactPhone: '', contactEmail: '' };

export default function OrganizerDashboard() {
  const { currentUser, isAdvanced, addTournament, updateTournament, deleteTournament } = useApp();
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [matches, setMatches] = useState([]);
  const [modal, setModal] = useState(false);
  const [ready, setReady] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [tFilter, setTFilter] = useState(null);

  const load = async () => {
    try {
      const res = await tournamentAPI.getOrganizerSummary();
      setTournaments(res.data.tournaments || []);
      setMatches(res.data.matches || []);
    } catch (e) {
      console.error('Failed to load dashboard:', e);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (!currentUser?.id) {
        console.log('No currentUser.id, skipping load');
        return;
      }
      const t0 = Date.now();
      await load();
      setTimeout(() => setReady(true), Math.max(0, 20 - (Date.now() - t0)));
    };
    init();
  }, [currentUser]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);

  if (!ready) return <OrganizerDashboardSkeleton />;

  const myT = tournaments; // Already filtered by backend
  const myM = matches; // Already filtered by backend

  const openNew = () => { setForm(emptyForm); setEditId(null); setModal(true); };
  const openEdit = t => { setForm({ name: t.name, type: t.type, matchType: t.matchType || 'Singles', ageGroup: t.ageGroup || 'Open', status: t.status, surface: t.surface, startDate: t.startDate, endDate: t.endDate, registrationDeadline: t.registrationDeadline ? t.registrationDeadline.split('T')[0] : '', location: t.location, prize: t.prize, winnerPrize: t.winnerPrize || '', runnerUpPrize: t.runnerUpPrize || '', entryFee: t.entryFee || 0, upiId: t.upiId || '', maxPlayers: t.maxPlayers, description: t.description, rules: t.rules || '', venueMap: t.venueMap || '', parking: t.parking || '', contactName: t.contactName || '', contactPhone: t.contactPhone || '', contactEmail: t.contactEmail || '' }); setEditId(t.id); setModal(true); };
  const handleSave = async (e) => { 
    e.preventDefault(); 
    if (editId) {
      await updateTournament(editId, form);
    } else {
      await addTournament({ ...form, organizerId: currentUser.id });
    }
    setModal(false);
    await load(); // Reload dashboard data
  };
  const handleDelete = async (tournament) => {
    await deleteTournament(tournament.id);
    setDeleteConfirm(null);
    await load(); // Reload dashboard data
  };
  const set = (k, v) => setForm(p => {
    const updated = { ...p, [k]: v };
    if (k === 'winnerPrize' || k === 'runnerUpPrize') {
      const w = parseInt(k === 'winnerPrize' ? v : updated.winnerPrize) || 0;
      const r = parseInt(k === 'runnerUpPrize' ? v : updated.runnerUpPrize) || 0;
      updated.prize = w + r > 0 ? `₹${(w + r).toLocaleString('en-IN')}` : '';
    }
    return updated;
  });

  return (
    <div className="container">
      <div style={s.header}>
        <div><h1 className="heading">MY EVENTS</h1><p className="sub-text">Manage tournaments, brackets, and scores</p></div>
        <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap'}}>
          <Link to="/organizer/group-stages" style={{textDecoration: 'none'}}>
            <button className="btn-primary" style={{background: '#007AFF', color: 'white'}}>📊 Group Stages</button>
          </Link>
          <button onClick={openNew} className="btn-primary">+ Create Tournament</button>
        </div>
      </div>
      <div className="org-stats">
        {[{ l: 'Tournaments', v: myT.length, c: '#1E88E5', icon: Icons.trophy }, { l: 'Matches', v: myM.length, c: '#43A047', icon: Icons.calendar }, { l: 'Live Now', v: myM.filter(m => m.status === 'live').length, c: '#E53935', icon: Icons.clock }].map(st => (
          <div key={st.l} className="org-stat-card">
            <div className="org-stat-icon" style={{ background: `${st.c}15` }}><Ico d={st.icon} size={20} color={st.c} /></div>
            <div>
              <div className="org-stat-val">{st.v}</div>
              <div className="org-stat-label">{st.l}</div>
            </div>
          </div>
        ))}
      </div>
      {myT.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 48 }}>
          <h3 style={{ color: 'var(--navy)', fontFamily: "'Outfit', sans-serif" }}>No Events Yet</h3>
          <p style={{ color: 'var(--gray-400)', fontSize: 13, marginTop: 4 }}>Create your first tournament to get started.</p>
          <button onClick={openNew} style={{ ...s.addBtn, marginTop: 12 }}>+ Create Tournament</button>
        </Card>
      ) : (() => {
        const ongoing = myT.filter(t => t.status === 'ongoing');
        const upcoming = myT.filter(t => t.status === 'upcoming');
        const completed = myT.filter(t => t.status === 'completed');
        const pills = [
          { key: 'ongoing', label: 'Ongoing', count: ongoing.length },
          { key: 'upcoming', label: 'Upcoming', count: upcoming.length },
          { key: 'completed', label: 'Completed', count: completed.length },
        ].filter(p => p.count > 0);
        const activePill = tFilter && pills.some(p => p.key === tFilter) ? tFilter : pills[0]?.key;
        if (activePill && activePill !== tFilter) setTFilter(activePill);
        const filtered = activePill === 'ongoing' ? ongoing : activePill === 'upcoming' ? upcoming : completed;
        if (pills.length === 0) return (
          <Card style={{ textAlign: 'center', padding: 48 }}>
            <h3 style={{ color: 'var(--navy)', fontFamily: "'Outfit', sans-serif" }}>No Events Yet</h3>
            <p style={{ color: 'var(--gray-400)', fontSize: 13, marginTop: 4 }}>Create your first tournament to get started.</p>
            <button onClick={openNew} style={{ ...s.addBtn, marginTop: 12 }}>+ Create Tournament</button>
          </Card>
        );
        return (
          <>
            <div className="pill-tabs">
              {pills.map(p => (
                <button key={p.key} onClick={() => setTFilter(p.key)} className={`pill-tab ${activePill === p.key ? 'pill-tab-active' : ''}`}>
                  {p.label}
                  <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 700, background: activePill === p.key ? 'rgba(189,255,0,0.2)' : '#EEF1F6', color: activePill === p.key ? '#BDFF00' : '#8892A6', padding: '1px 6px', borderRadius: 10 }}>{p.count}</span>
                </button>
              ))}
            </div>
            {filtered.length === 0 ? (
              <Card style={{ textAlign: 'center', padding: 32 }}>
                <p style={{ color: 'var(--gray-400)', fontSize: 13, margin: 0 }}>No {activePill} tournaments</p>
              </Card>
            ) : (
              <div className="org-grid">{filtered.map(t => {
          const tm = matches.filter(m => String(m.tournamentId) === String(t.id || t._id));
          const live = tm.filter(m => m.status === 'live').length;
          return (
            <div key={t.id} style={{ position: 'relative', cursor: 'pointer' }} onClick={() => navigate(`/organizer/tournament/${t.id}`)}>
              <TournamentCard tournament={t} showActions={true} />
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex', gap: 8, background: 'var(--card-bg)', padding: '12px 16px', borderRadius: '0 0 16px 16px', borderTop: '1px solid var(--card-border)' }}>
                <Link to={`/organizer/tournament/${t.id}`} className="org-manage-btn" onClick={(e) => e.stopPropagation()} style={{ flex: 1, textAlign: 'center', textDecoration: 'none' }}>Manage</Link>
                {t.status !== 'completed' && <button onClick={(e) => { e.stopPropagation(); openEdit(t); }} className="org-edit-btn" style={{ flex: 1 }}>Edit</button>}
                {t.status === 'upcoming' && <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm(t); }} className="org-delete-btn" style={{ flex: 1 }}>Delete</button>}
              </div>
            </div>
          );
        })}</div>
            )}
          </>
        );
      })()}
      <Modal isOpen={modal} onClose={() => setModal(false)} title={editId ? 'Edit Tournament' : 'Create Tournament'}>
        <form onSubmit={handleSave}>
          {/* Section: Basic Info */}
          <div style={s.sectionLabel} className="form-section"><Ico d={Icons.trophy} size={14} color="var(--navy)" style={{ display: 'inline-block', marginRight: 6 }} /> Basic Info</div>
          <div className="form-grid">
            <div className="form-span-full"><label className="form-label">Tournament Name</label><input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} required placeholder="e.g. Sangvi Open 2025" /></div>
            <div><label className="form-label">Format</label><select className="form-input" value={form.type} onChange={e => set('type', e.target.value)}><option>Knockout</option><option>Round Robin</option></select></div>
            <div><label className="form-label">Match Type</label><select className="form-input" value={form.matchType} onChange={e => set('matchType', e.target.value)}><option>Singles</option><option>Doubles</option></select></div>
            <div><label className="form-label">Age Group</label><select className="form-input" value={form.ageGroup} onChange={e => set('ageGroup', e.target.value)}><option>Open</option><option>Under 12</option><option>Under 14</option><option>Under 16</option><option>Under 18</option><option>Under 21</option><option>35+</option><option>45+</option><option>55+</option></select></div>
            <div><label className="form-label">Max Players</label><select className="form-input" value={form.maxPlayers} onChange={e => set('maxPlayers', parseInt(e.target.value))}><option value={4}>4</option><option value={8}>8</option><option value={16}>16</option>{(isAdvanced || form.maxPlayers === 32 || form.maxPlayers === 64) && <option value={32}>32</option>}{(isAdvanced || form.maxPlayers === 64) && <option value={64}>64</option>}</select></div>
          </div>

          {/* Section: Venue & Schedule */}
          <div style={s.sectionLabel} className="form-section"><Ico d={Icons.pin} size={14} color="var(--navy)" style={{ display: 'inline-block', marginRight: 6 }} /> Venue & Schedule</div>
          <div className="form-grid">
            <div className="form-span-full"><label className="form-label">Location</label><input className="form-input" value={form.location} onChange={e => set('location', e.target.value)} required placeholder="e.g. Balewadi, Pune" /></div>
            <div><label className="form-label">Surface</label><select className="form-input" value={form.surface} onChange={e => set('surface', e.target.value)}><option>Hard Court</option><option>Clay</option><option>Grass</option><option>Indoor</option></select></div>
            <div></div>
            <div><label className="form-label">Registration Deadline</label><input className="form-input" type="date" value={form.registrationDeadline} onChange={e => set('registrationDeadline', e.target.value)} /></div>
            <div></div>
            <div><label className="form-label">Start Date</label><input className="form-input" type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} required /></div>
            <div><label className="form-label">End Date</label><input className="form-input" type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)} required /></div>
          </div>

          {/* Section: Prize & Fees */}
          <div style={s.sectionLabel} className="form-section"><span style={{ fontSize: 14 }}>💰</span> Prize & Fees</div>
          <div className="form-grid">
            <div><label className="form-label">Total Prize (₹)</label><input className="form-input" value={form.prize} readOnly style={{ background: 'var(--gray-50)', color: 'var(--navy)', fontWeight: 700, cursor: 'default' }} placeholder="Auto-calculated" /></div>
            <div><label className="form-label">Entry Fee (₹)</label><input className="form-input" type="number" min="0" value={form.entryFee} onChange={e => set('entryFee', parseInt(e.target.value) || 0)} /></div>
            <div><label className="form-label">Winner Prize (₹)</label><input className="form-input" value={form.winnerPrize} onChange={e => set('winnerPrize', e.target.value)} placeholder="₹30,000" /></div>
            <div><label className="form-label">Runner-up Prize (₹)</label><input className="form-input" value={form.runnerUpPrize} onChange={e => set('runnerUpPrize', e.target.value)} placeholder="₹15,000" /></div>
          </div>

          {/* Section: Payment */}
          <div style={s.sectionLabel} className="form-section"><span style={{ fontSize: 14 }}>📱</span> Payment</div>
          <div className="form-grid">
            <div className="form-span-full"><label className="form-label">UPI ID (for receiving payments)</label><input className="form-input" value={form.upiId} onChange={e => set('upiId', e.target.value)} placeholder="yourname@upi" /></div>
          </div>

          {/* Section: Description */}
          <div style={s.sectionLabel} className="form-section"><span style={{ fontSize: 14 }}>📝</span> Description</div>
          <textarea className="form-input" style={{ minHeight: 70 }} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Tell players about this tournament..." />

          {/* Section: Rules & Venue Info */}
          <div style={s.sectionLabel} className="form-section"><Ico d={Icons.clipboard} size={14} color="var(--navy)" style={{ display: 'inline-block', marginRight: 6 }} /> Rules & Venue Info</div>
          <textarea className="form-input" style={{ minHeight: 70, marginBottom: 10 }} value={form.rules} onChange={e => set('rules', e.target.value)} placeholder="Tournament rules (e.g. best of 3 sets, tiebreak at 6-6...)" />
          <div className="form-grid">
            <div className="form-span-full"><label className="form-label">Venue / Map Link</label><input className="form-input" value={form.venueMap} onChange={e => set('venueMap', e.target.value)} placeholder="Google Maps link or venue directions" /></div>
            <div className="form-span-full"><label className="form-label">Parking Info</label><input className="form-input" value={form.parking} onChange={e => set('parking', e.target.value)} placeholder="e.g. Free parking available at Gate 2" /></div>
            <div><label className="form-label">Contact Name</label><input className="form-input" value={form.contactName} onChange={e => set('contactName', e.target.value)} placeholder="e.g. Suraj Roy" /></div>
            <div><label className="form-label">Contact Phone</label><input className="form-input" value={form.contactPhone} onChange={e => set('contactPhone', e.target.value)} placeholder="+91 98765 43210" /></div>
            <div><label className="form-label">Contact Email</label><input className="form-input" value={form.contactEmail} onChange={e => set('contactEmail', e.target.value)} placeholder="organizer@email.com" /></div>
          </div>

          <button type="submit" className="btn-primary" style={{ width: '100%', marginTop: 18, padding: '12px 20px', borderRadius: 10, fontWeight: 800, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>{editId ? <><Ico d={Icons.check} size={14} /> Update Tournament</> : <><Ico d={Icons.plus} size={14} /> Create Tournament</>}</button>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => handleDelete(deleteConfirm)}
        title="Delete Tournament"
        message={`Are you sure you want to delete "${deleteConfirm?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        icon={<Ico d={Icons.trash} size={24} color="#E53935" />}
      />
    </div>
  );
}


const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 10 },
  addBtn: { padding: '10px 20px', background: 'var(--navy)', color: 'var(--lime)', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 12 },
  sectionLabel: {},
};
